<script setup>
import ActionButton from '@/components/shared/buttons/ActionButton.vue'
import TransitionFadeIn from '@/components/shared/transitions/TransitionFadeIn.vue'
import {useThrottleFn} from '@vueuse/core'
import {ref, useTemplateRef} from 'vue'

//Emits and props
defineProps({
  showJumpToBottom: {
    type: Boolean,
    default: false,
  },
})

//Expose scroll helper
const scrollContainer = useTemplateRef('scrollContainer')
const scrollToBottom = useThrottleFn(() => {
  isNearBottom.value = true
  scrollContainer.value?.scroll({
    top: scrollContainer.value.scrollHeight,
    behavior: 'instant',
  })
}, 250)

//Helper to get view port height more reliably
const getViewportHeight = () => {
  return window.innerHeight ||
         document.documentElement.clientHeight ||
         document.body.clientHeight ||
         800 // fallback
}

//Check if we're near the bottom of the scroll container and if the scroll container is at least twice the height of the viewport
const isNearBottom = ref(false)
const hasMinHeight = ref(false)
const onScroll = () => {
  const viewportHeight = getViewportHeight()
  isNearBottom.value = scrollContainer.value?.scrollTop + scrollContainer.value?.clientHeight >= scrollContainer.value?.scrollHeight - viewportHeight
  hasMinHeight.value = scrollContainer.value?.scrollHeight >= viewportHeight * 2
}

//Debounced handler
const onScrollThrottled = useThrottleFn(onScroll, 100)
</script>

<template>
  <div
    ref="scrollContainer"
    scrollbar-hidden
    @scroll="onScrollThrottled()"
  >
    <TransitionFadeIn>
      <ActionButton
        v-if="showJumpToBottom && hasMinHeight && !isNearBottom"
        :class="$style.jumpToBottom"
        label="Jump to bottom"
        type="primary"
        icon="arrow_downward"
        is-slim
        @click="scrollToBottom()"
      />
    </TransitionFadeIn>
    <div :class="$style.messages">
      <TransitionGroup
        :enter-from-class="$style.from"
        :enter-active-class="$style.active"
        :enter-to-class="$style.to"
        :leave-from-class="$style.to"
        :leave-active-class="$style.active"
        :leave-to-class="$style.from"
        appear
      >
        <slot :scroll-to-bottom="scrollToBottom" />
      </TransitionGroup>
    </div>
    <slot name="after-messages" />
  </div>
</template>

<style module>
.messages {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0 auto;
}
.jumpToBottom {
  position: absolute;
  z-index: 1000;
  bottom: 6rem;
  right: 1rem;
  box-shadow: var(--box-shadow-strong);
  @media (min-width: 768px) {
    bottom: 8rem;
    right: 2rem;
  }
}

/* Transition */
.active {
  transition: opacity var(--transition-cubic);
}
.from {
  opacity: 0;
}
.to {
  opacity: 1;
}
</style>
